const ROOT_DOMAIN = 'getcartwheel.com';

/** Slugs are lowercase alphanumerics; hyphens only appear in the environment suffix. */
const CLIENT_SLUG_PATTERN = /^[a-z0-9]+$/;

/** Tenant hosts are `{slug}{disambiguator}.getcartwheel.com`, e.g. `acme-staging`. */
const ENVIRONMENT_HOST_SUFFIXES = ['-staging', '-local'];

/**
 * Hostnames that belong to Cartwheel itself, so they never resolve to a tenant
 * even though they sit in the same subdomain position as a dedicated login host.
 */
export const RESERVED_CLIENT_SLUGS = [
  'www',
  'studio',
  'app',
  'docs',
  'resources',
  'auth',
  'api',
  'labeler',
  'staging',
  'local',
  'mail',
  'cdn',
  'getcartwheel',
] as const;

const reservedSlugs = new Set<string>(RESERVED_CLIENT_SLUGS);

/**
 * Returns the tenant slug a dedicated login host stands for, or `undefined` for
 * the apex site, Cartwheel-owned subdomains, and anything off `getcartwheel.com`.
 *
 * `acme.getcartwheel.com` and `acme-staging.getcartwheel.com` → `acme`.
 */
export function parseClientSlugFromHostname(
  hostname: string | undefined,
): string | undefined {
  const host = (hostname ?? '').trim().toLowerCase().split(':')[0];
  const suffix = `.${ROOT_DOMAIN}`;
  if (!host.endsWith(suffix)) {
    return undefined;
  }

  const labels = host.slice(0, -suffix.length).split('.');
  if (labels.length !== 1) {
    return undefined;
  }

  let slug = labels[0];
  for (const environmentSuffix of ENVIRONMENT_HOST_SUFFIXES) {
    if (slug.endsWith(environmentSuffix)) {
      slug = slug.slice(0, -environmentSuffix.length);
      break;
    }
  }

  if (!CLIENT_SLUG_PATTERN.test(slug) || reservedSlugs.has(slug)) {
    return undefined;
  }
  return slug;
}

/** Tenant slug for the host the SPA is currently served from, if there is one. */
export function getCurrentClientSlug(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return parseClientSlugFromHostname(window.location.hostname);
}

/**
 * Copies the dedicated login host's tenant onto a URL as `client={slug}`.
 * Stytch redirects land on the auth API instead of the login host, so the slug
 * has to travel in the query string for the backend to route the first session.
 */
export function appendClientQueryParam(
  url: string,
  slug = getCurrentClientSlug(),
): string {
  if (!slug) {
    return url;
  }
  return `${url}${url.includes('?') ? '&' : '?'}client=${slug}`;
}
